import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const movieId = Number(id);
  const movie = useMemo(() => movies.find((m) => m.id === movieId), [movieId]);

  const relatedMovies = useMemo(() => {
    if (!movie) return [];
    return movies
      .filter((m) => m.id !== movie.id && m.categorySlug === movie.categorySlug)
      .slice(0, 5);
  }, [movie]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" as const });
  }, [id]);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            </div>
            <h2 className="mt-6 text-xl font-semibold text-foreground-950">电影未找到</h2>
            <p className="mt-2 text-sm text-foreground-500">该电影不存在或已下架</p>
            <button
              onClick={() => {
                navigate("/");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="mt-6 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-full transition-colors cursor-pointer"
            >
              返回首页
            </button>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="pt-20 min-h-screen bg-background-50">
        {/* Breadcrumb */}
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 pt-6">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="hover:text-primary-500 transition-colors"
            >
              首页
            </a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <a
              href={`/category/${movie.categorySlug}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/category/${movie.categorySlug}`);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="hover:text-primary-500 transition-colors"
            >
              {movie.category}
            </a>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-700 font-medium line-clamp-1">
              {movie.title}
            </span>
          </nav>
        </div>

        {/* Movie Banner */}
        <section className="relative w-full h-[350px] md:h-[500px] overflow-hidden mt-4">
          <img
            src={movie.banner}
            alt={movie.title}
            title={`${movie.title} - 在线观看高清电影`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-black/20 to-black/30"></div>
        </section>

        {/* Movie Info */}
        <section className="relative z-10 -mt-32 md:-mt-40">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              {/* Poster */}
              <div className="flex-shrink-0 w-40 md:w-56 mx-auto md:mx-0">
                <div className="aspect-[2/3] rounded-xl overflow-hidden shadow-lg">
                  <img
                    src={movie.image}
                    alt={movie.title}
                    title={`${movie.title} 海报 - 青柠影院`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Details */}
              <div className="flex-1 pt-4">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {movie.year} / {movie.category} / {movie.duration}
                </p>

                {/* Rating */}
                <div className="flex items-center gap-4 mt-4">
                  <div className="flex items-center gap-1.5">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100">
                      <i className="ri-star-fill text-amber-500 text-lg"></i>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-foreground-950">{movie.rating}</div>
                      <div className="text-xs text-foreground-500">豆瓣评分</div>
                    </div>
                  </div>
                </div>

                {/* Director & Cast */}
                <div className="mt-6 space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-medium text-foreground-700 min-w-[60px] whitespace-nowrap">导演：</span>
                    <span className="text-sm text-foreground-600">{movie.director}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-sm font-medium text-foreground-700 min-w-[60px] whitespace-nowrap">主演：</span>
                    <span className="text-sm text-foreground-600">{movie.cast.join(" / ")}</span>
                  </div>
                </div>

                {/* Play Button */}
                <div className="flex items-center gap-3 mt-6">
                  <button
                    className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm flex items-center gap-2 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即播放
                  </button>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer"
                    aria-label="收藏"
                  >
                    <i className="ri-heart-line text-lg"></i>
                  </button>
                  <button
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer"
                    aria-label="分享"
                  >
                    <i className="ri-share-line text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="max-w-[1440px] mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="bg-background-50 border border-background-200 rounded-xl p-6 md:p-8">
            <h2 className="text-lg font-semibold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-file-text-line text-primary-500"></i>
              剧情简介
            </h2>
            <p className="text-sm text-foreground-700 leading-relaxed">
              {movie.longDescription}
            </p>
          </div>
        </section>

        {/* Related Movies */}
        {relatedMovies.length > 0 && (
          <section className="max-w-[1440px] mx-auto px-4 md:px-6 pb-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground-950 flex items-center gap-2">
                <i className="ri-movie-2-line text-primary-500"></i>
                同类推荐
              </h2>
              <a
                href={`/category/${movie.categorySlug}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/category/${movie.categorySlug}`);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="text-sm text-primary-500 hover:text-primary-600 transition-colors font-medium flex items-center gap-1 whitespace-nowrap"
              >
                更多{movie.category}
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {relatedMovies.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />

      {/* Schema.org - Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${movie.title} - 青柠影院`,
            description: movie.description,
            datePublished: `${movie.year}-01-01`,
            author: {
              "@type": "Person",
              name: movie.director,
            },
            publisher: {
              "@type": "Organization",
              name: "青柠影院",
            },
          }),
        }}
      />
      {/* Schema.org - BreadcrumbList */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: movie.category,
              },
              {
                "@type": "ListItem",
                position: 3,
                name: movie.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}